import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import WorkflowRunsEmptyState from "src/views/products/workflow/components/WorkflowRunsEmptyState";
import Markdown from "react-markdown";
import { nuclearn_api_url } from "src/App";
import { itemEquivalencyChatUrl } from "./itemEquivalencyProduct";

import WorkflowRunsPage, {
  type WorkflowRunsColumn,
} from "src/views/products/workflow/pages/WorkflowRunsPage";
import {
  getPackageMarkdown,
  useItemEquivalencyRuns,
  type ItemEquivalencyRunListItem,
} from "./services/ItemEquivalencyService";
import { VERDICT_COLORS, VERDICT_LABELS } from "./verdict";

type DerivedStatus = {
  key: "complete" | "concluded" | "in_progress";
  label: string;
  color: "default" | "primary" | "success" | "warning";
};

/**
 * Status is derived, never stored: the record's raw columns are the truth
 * (status flips to complete when the package is recorded, conclusion is the
 * computed verdict), so there is no lifecycle column to keep honest.
 */
const deriveStatus = (run: ItemEquivalencyRunListItem): DerivedStatus => {
  if (run.report_artifact_fk || run.status === "complete") {
    return { key: "complete", label: "Complete", color: "success" };
  }
  if (run.conclusion) {
    return {
      key: "concluded",
      label: "Concluded — package pending",
      color: "warning",
    };
  }
  return { key: "in_progress", label: "In progress", color: "primary" };
};


/** The recorded §3.5.4 package. New records store a DOCX (download-only);
 * pre-DOCX records stored markdown, which still renders inline. Detection is
 * by content shape: a DOCX body starts with the ZIP magic "PK". */
const PackagePreviewDialog = ({
  run,
  onClose,
}: {
  run: ItemEquivalencyRunListItem;
  onClose: () => void;
}) => {
  const [content, setContent] = useState<string | null>(null);
  const [isBinary, setIsBinary] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!run.report_artifact_fk) return;
    let cancelled = false;
    getPackageMarkdown(run.report_artifact_fk)
      .then((body) => {
        if (cancelled) return;
        if (body.startsWith("PK")) setIsBinary(true);
        else setContent(body);
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [run.report_artifact_fk]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{run.title}</DialogTitle>
      <DialogContent dividers data-testid="peq-package-preview">
        {error ? (
          <Typography color="error">
            Could not load the package document.
          </Typography>
        ) : isBinary ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography sx={{ mb: 2 }}>
              The recorded package is a Word document.
            </Typography>
            <Button
              variant="contained"
              href={`${nuclearn_api_url}/artifacts/${run.report_artifact_fk}/download`}
              data-testid="peq-package-download"
            >
              Download package (.docx)
            </Button>
          </Box>
        ) : content === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ "& h1, & h2, & h3": { mt: 2 } }}>
            <Markdown>{content}</Markdown>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-testid="peq-package-preview-close" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Past item-equivalency evaluations on the shared workflow runs frame. */
const ItemEquivalencyRunsPage = () => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState<string | number | "">("");
  const [verdictFilter, setVerdictFilter] = useState<string | number | "">("");
  const [previewRun, setPreviewRun] =
    useState<ItemEquivalencyRunListItem | null>(null);

  const {
    data: runs = [],
    isFetching,
    isError,
    refetch,
  } = useItemEquivalencyRuns();

  const filteredRuns = useMemo(
    () =>
      runs.filter(
        (run) =>
          (statusFilter === "" || deriveStatus(run).key === statusFilter) &&
          (verdictFilter === "" || run.conclusion === verdictFilter),
      ),
    [runs, statusFilter, verdictFilter],
  );

  const columns = useMemo<WorkflowRunsColumn<ItemEquivalencyRunListItem>[]>(
    () => [
      {
        key: "title",
        header: "Evaluation",
        render: (run) => (
          <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
            {run.title}
          </Typography>
        ),
      },
      {
        key: "started",
        header: "Started",
        render: (run) =>
          run.create_date
            ? new Date(run.create_date).toLocaleDateString()
            : "—",
      },
      {
        key: "status",
        header: "Status",
        render: (run) => {
          const status = deriveStatus(run);
          return (
            <Chip
              label={status.label}
              size="small"
              color={status.color}
              variant="outlined"
            />
          );
        },
      },
      {
        key: "verdict",
        header: "Verdict",
        render: (run) =>
          run.conclusion ? (
            <Chip
              label={VERDICT_LABELS[run.conclusion] ?? run.conclusion}
              size="small"
              color={VERDICT_COLORS[run.conclusion] ?? "default"}
              variant="outlined"
            />
          ) : (
            "—"
          ),
      },
      {
        key: "open",
        header: "Open items",
        align: "right",
        render: (run) => run.open_item_count ?? "—",
      },
      {
        key: "package",
        header: "Package",
        render: (run) =>
          run.report_artifact_fk ? (
            <Button
              size="small"
              startIcon={<ArticleOutlinedIcon />}
              onClick={(event) => {
                event.stopPropagation();
                setPreviewRun(run);
              }}
              data-testid={`peq-run-package-${run.record_id}`}
            >
              Preview
            </Button>
          ) : (
            "—"
          ),
      },
      {
        key: "chat",
        header: "",
        align: "right",
        render: (run) =>
          run.dialogue_id ? <ForumOutlinedIcon fontSize="small" /> : null,
      },
    ],
    [],
  );

  return (
    <WorkflowRunsPage
      title="Item Equivalency Runs"
      testIdPrefix="peq"
      filters={[
        {
          key: "status",
          label: "Status",
          options: [
            { value: "in_progress", label: "In progress" },
            { value: "concluded", label: "Concluded — package pending" },
            { value: "complete", label: "Complete" },
          ],
          value: statusFilter,
          onChange: setStatusFilter,
        },
        {
          key: "verdict",
          label: "Verdict",
          options: Object.entries(VERDICT_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          value: verdictFilter,
          onChange: setVerdictFilter,
        },
      ]}
      columns={columns}
      rows={filteredRuns}
      rowKey={(run) => run.record_id}
      isLoading={isFetching}
      isError={isError}
      onRefresh={() => refetch()}
      onRowClick={(run) =>
        run.dialogue_id && history.push(itemEquivalencyChatUrl(run.dialogue_id))
      }
      emptyState={
        <>
          <WorkflowRunsEmptyState
            message="No evaluations yet. Start one from the"
            linkHref="/parts/compare"
            linkLabel="comparison page"
            onNavigate={() => history.push("/parts/compare")}
          />
          .
        </>
      }
      detail={
        previewRun && (
          <PackagePreviewDialog
            run={previewRun}
            onClose={() => setPreviewRun(null)}
          />
        )
      }
    />
  );
};

export default ItemEquivalencyRunsPage;
